import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag, Truck } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { formatINR, waLink, buildOrderMessage } from "@/config";

export const CartDrawer = () => {
  const navigate = useNavigate();
  const {
    cart, cartOpen, setCartOpen, updateQty, removeFromCart,
    subtotal, discount, shipping, total,
  } = useStore();

  const remainingForFree = Math.max(0, 1500 - (subtotal - discount));
  const pct = Math.min(100, ((subtotal - discount) / 1500) * 100);

  const whatsappOrder = () => {
    const msg = buildOrderMessage({
      items: cart, subtotal, discount, shipping, total,
    });
    window.open(waLink(msg), "_blank");
  };

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="w-full sm:max-w-md flex flex-col p-0 bg-[#FAF6F0]" data-testid="cart-drawer">
        <SheetHeader className="px-5 py-4 border-b border-[#E7E0D6] bg-white">
          <SheetTitle className="font-display text-xl flex items-center gap-2">
            <ShoppingBag size={20} className="text-[#C2410C]" /> Your Cart ({cart.length})
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-8">
            <div>
              <ShoppingBag size={54} className="mx-auto text-[#D6C9BA]" />
              <p className="mt-4 text-[#6B5B52] font-medium">Your cart is empty</p>
              <button
                onClick={() => { setCartOpen(false); navigate("/shop"); }}
                data-testid="cart-empty-shop-btn"
                className="mt-5 rounded-full bg-[#C2410C] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#9A3412]"
              >
                Start Shopping
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 py-3 bg-[#FEF3C7]/60 border-b border-[#FDE68A]">
              <div className="flex items-center gap-2 text-xs font-medium text-[#9A3412]">
                <Truck size={15} />
                {remainingForFree > 0 ? (
                  <span>Add <b>{formatINR(remainingForFree)}</b> more for FREE shipping</span>
                ) : (
                  <span>🎉 You've unlocked FREE shipping!</span>
                )}
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-white overflow-hidden">
                <div className="h-full bg-[#15803D] transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {cart.map((it) => (
                <div key={it.key} className="flex gap-3" data-testid={`cart-item-${it.slug}`}>
                  <img src={it.image} alt={it.name} className="h-20 w-20 rounded-xl object-cover border border-[#E7E0D6]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-[#1F130B] leading-snug truncate">{it.name}</p>
                    <p className="text-xs text-[#6B5B52]">{it.weight_label}</p>
                    <p className="text-sm font-bold text-[#C2410C] mt-0.5">{formatINR(it.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center border border-[#E7E0D6] rounded-full bg-white">
                        <button onClick={() => updateQty(it.key, it.qty - 1)} data-testid={`cart-dec-${it.slug}`} className="h-7 w-7 grid place-items-center text-[#6B5B52]"><Minus size={13} /></button>
                        <span className="w-7 text-center text-sm font-semibold">{it.qty}</span>
                        <button onClick={() => updateQty(it.key, it.qty + 1)} data-testid={`cart-inc-${it.slug}`} className="h-7 w-7 grid place-items-center text-[#6B5B52]"><Plus size={13} /></button>
                      </div>
                      <button onClick={() => removeFromCart(it.key)} data-testid={`cart-remove-${it.slug}`} className="text-[#9CA3AF] hover:text-[#C2410C]"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E7E0D6] bg-white px-5 py-4 space-y-3">
              <div className="flex justify-between text-sm text-[#6B5B52]">
                <span>Subtotal</span><span className="font-semibold text-[#1F130B]">{formatINR(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-sm text-[#15803D]">
                  <span>Discount</span><span className="font-semibold">-{formatINR(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-[#6B5B52]">
                <span>Shipping</span><span className="font-semibold">{shipping ? formatINR(shipping) : "FREE"}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-[#1F130B] pt-2 border-t border-dashed border-[#E7E0D6]">
                <span>Total</span><span>{formatINR(total)}</span>
              </div>
              <button
                onClick={() => { setCartOpen(false); navigate("/checkout"); }}
                data-testid="cart-checkout-btn"
                className="w-full rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-semibold py-3 transition-colors"
              >
                Proceed to Checkout
              </button>
              <button
                onClick={whatsappOrder}
                data-testid="cart-whatsapp-btn"
                className="w-full rounded-full bg-[#25D366] hover:brightness-95 text-white font-semibold py-3 transition"
              >
                Order on WhatsApp
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
